import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  AppBar, Toolbar, Typography, IconButton, Button, Drawer,
  SwipeableDrawer, List, ListItemButton, ListItemIcon, ListItemText,
  Divider, Collapse, Tooltip,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers, faCheck, faArrowLeft, faBook, faHandshake,
  faBalanceScale, faFileSignature, faGraduationCap, faAward,
  faEnvelope, faUserGraduate, faPowerOff, faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';

import axios from '../../services/api';
import { MEIS_EDU_TOKEN } from '../../services/api';
import dataService from '../../services/dataService';
import { loginSuccess, logout } from '../../store/authSlice';
import { setClasses, setCurrClass } from '../../store/classSlice';
import Spinner from '../Spinner/Spinner';
import MarksMUI from './Marks/MarksMUI';
import MarksDetailsMUI from './Marks/MarksDetailsMUI';
import Conduct from './Conduct/Conduct';
import Attendance from './Attendance/Attendance';
import Discipline from './Discipline/Discipline';
import ExtTest from './ExtTest/ExtTest';
import ParentCoop from './Parents/ParentCoop';
import Notifications from './Notifications/Notifications';
import Contact from './Contact/Contact';
import BackgroundHeader from './header.png';
import MemoImg from './img/memo.png';
import LogoImg from './img/Telekom logo magenta 2022.png';

const drawerWidth = 280;

export default function Main() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { user } = useSelector((s) => s.authentication);
  const { class_id, class_name, class_data } = useSelector((s) => s.school_classes);

  const [loading, setLoading] = useState(false);
  const [marks, setMarks] = useState([]);
  const [markTypes, setMarkTypes] = useState([]);
  const [marksDetails, setMarksDetails] = useState([]);
  const [conduct, setConduct] = useState([]);
  const [conductLoaded, setConductLoaded] = useState(false);
  const [attendance, setAttendance] = useState([]);
  const [attendanceLoaded, setAttendanceLoaded] = useState(false);
  const [discipline, setDiscipline] = useState([]);
  const [disciplineLoaded, setDisciplineLoaded] = useState(false);
  const [extTest, setExtTest] = useState([]);
  const [extTestLoaded, setExtTestLoaded] = useState(false);
  const [parentCoop, setParentCoop] = useState([]);
  const [parentCoopLoaded, setParentCoopLoaded] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoaded, setNotificationsLoaded] = useState(false);

  const [left, setLeft] = useState(false);
  const [selectedKey, setSelectedKey] = useState('marks');
  const [typeId, setTypeId] = useState(-1);
  const [subjectSelected, setSubjectSelected] = useState(false);
  const [subjectName, setSubjectName] = useState('');
  const [title, setTitle] = useState('Ocjene');
  const [classOpen, setClassOpen] = useState(false);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = () => {
    setLoading(true);
    axios.get('/api/getuser')
      .then((response) => {
        setLoading(false);
        const u = response.data;
        dispatch(loginSuccess(u.username));
        dispatch(setClasses(u.classes));
        setMarks(u.marks);
        setMarkTypes(u.markTypes);
        if (u.is_admin) navigate('/admin');
      })
      .catch(() => {
        setLoading(false);
        navigate('/login');
      });
  };

  const loadMarks = (cid) => {
    setLoading(true);
    setTimeout(async () => {
      try {
        const m = await dataService.loadMarks(cid);
        setMarks(m);
      } catch {}
      setLoading(false);
    }, 500);
  };

  const loadNewClass = (cid) => {
    setMarksDetails([]);
    setConduct([]); setConductLoaded(false);
    setAttendance([]); setAttendanceLoaded(false);
    setDiscipline([]); setDisciplineLoaded(false);
    setExtTest([]); setExtTestLoaded(false);
    setParentCoop([]); setParentCoopLoaded(false);
    setNotifications([]); setNotificationsLoaded(false);
    setSelectedKey('marks');
    setTypeId(-1);
    setSubjectSelected(false);
    setSubjectName('');
    setTitle('Ocjene');
    setClassOpen(false);
    setLeft(false);
    loadMarks(cid);
  };

  const handleLogout = () => {
    localStorage.setItem(MEIS_EDU_TOKEN, null);
    dispatch(logout());
    dispatch(setClasses());
    navigate('/login');
  };

  const loadSection = useCallback(async (key) => {
    const loaders = {
      conduct: { loaded: conductLoaded, setLoaded: setConductLoaded, load: dataService.loadConduct, set: setConduct },
      attendance: { loaded: attendanceLoaded, setLoaded: setAttendanceLoaded, load: dataService.loadAttendance, set: setAttendance },
      discipline: { loaded: disciplineLoaded, setLoaded: setDisciplineLoaded, load: dataService.loadDiscipline, set: setDiscipline },
      ext_test: { loaded: extTestLoaded, setLoaded: setExtTestLoaded, load: dataService.loadExtTest, set: setExtTest },
      notifications: { loaded: notificationsLoaded, setLoaded: setNotificationsLoaded, load: dataService.loadNotifications, set: setNotifications },
      parent_coop: { loaded: parentCoopLoaded, setLoaded: setParentCoopLoaded, load: dataService.loadParentCoop, set: (d) => setParentCoop(d?.[0] || {}) },
    };

    const loader = loaders[key];
    if (!loader || loader.loaded) return;
    loader.setLoaded(true);
    setLoading(true);
    setTimeout(async () => {
      try {
        const data = await loader.load(class_id);
        if (data) loader.set(data);
      } catch {}
      setLoading(false);
    }, 500);
  }, [class_id, conductLoaded, attendanceLoaded, disciplineLoaded, extTestLoaded, notificationsLoaded, parentCoopLoaded]);

  const handleOptionSelected = (key, isPopup) => {
    setSelectedKey(key);
    const titles = {
      marks: 'Ocjene', conduct: 'Vladanje', attendance: 'Izostanci',
      discipline: 'Vaspitne mjere', ext_test: 'Eksterna testiranja',
      notifications: 'Informacije', parent_coop: 'Saradnja sa roditeljima',
      contact: 'Kontakt',
    };
    if (key === 'marks') setSubjectSelected(false);
    if (key === 'logout') { handleLogout(); return; }
    if (titles[key]) setTitle(titles[key]);
    if (['conduct', 'attendance', 'discipline', 'ext_test', 'notifications', 'parent_coop'].includes(key)) {
      loadSection(key);
    }
    if (isPopup) setLeft(false);
  };

  const jeModularnaOcjena = (ocjena) => {
    if (('naziv_cilja' in ocjena) && ['1','2','3','4','5'].includes(String(ocjena.ocjena)))
      return true; 
    else 
      return false; 
  }

  const onChangeType = (newTypeId) => {
    const updated = marks.map((mark) => {
      if (!mark.ocjene) return mark;
      const m = { ...mark, ocjena: '', datum: '', nazivtipa: '', naziv_cilja: '' };
      if (newTypeId == -1) {
        if (mark.ocjene.length > 0) {
          const o = mark.ocjene[0];
          m.ocjena = o.ocjena; m.datum = o.datum;
          m.nazivtipa = o.nazivtipa; m.naziv_cilja = o.naziv_cilja;
        }
      } else {
        for (const o of mark.ocjene) {
          if (o.id_tipa_ocjene == newTypeId && !jeModularnaOcjena(o)) {
            m.ocjena = o.ocjena; m.datum = o.datum;
            m.nazivtipa = o.nazivtipa; m.naziv_cilja = o.naziv_cilja;
          }
        }
      }
      return m;
    });
    setMarks(updated);
    setTypeId(newTypeId);
  };

  const handleRowSelected = (data) => {
    setMarksDetails(data.ocjene);
    setSubjectSelected(true);
    setSubjectName(data.naziv);
  };

  const handleClassClick = (item) => {
    setClassOpen(!classOpen);
    if (item) {
      dispatch(setCurrClass({ class_id: item.id, class_data }));
      loadNewClass(item.id);
    }
  };

  const menuItems = [
    { key: 'marks', icon: faGraduationCap, label: 'Ocjene' },
    { key: 'attendance', icon: faBook, label: 'Izostanci' },
    { key: 'conduct', icon: faFileSignature, label: 'Vladanje' },
    { key: 'discipline', icon: faBalanceScale, label: 'Vaspitne mjere' },
    { key: 'ext_test', icon: faAward, label: 'Eksterna testiranja' },
    { key: 'parent_coop', icon: faHandshake, label: 'Saradnja sa roditeljima' },
    { key: 'notifications', icon: faInfoCircle, label: 'Informacije' },
    { key: 'contact', icon: faEnvelope, label: 'Kontakt' },
  ];

  const renderMenuList = (isPopup) => (
    <List>
      {menuItems.map((item) => (
        <ListItemButton key={item.key} selected={selectedKey === item.key}
          onClick={() => handleOptionSelected(item.key, isPopup)}>
          <ListItemIcon className="centered-container-row icon-left-margin">
            <FontAwesomeIcon icon={item.icon} size="1x" />
          </ListItemIcon>
          <ListItemText primary={item.label} />
        </ListItemButton>
      ))}
    </List>
  );

  const renderLogoutList = () => (
    <List>
      <ListItemButton onClick={() => handleOptionSelected('logout')}>
        <ListItemIcon className="centered-container-row icon-left-margin">
          <FontAwesomeIcon icon={faPowerOff} size="1x" />
        </ListItemIcon>
        <ListItemText primary="Odjavi se" />
      </ListItemButton>
    </List>
  );

  const renderClassList = () => (
    <List>
      <ListItemButton onClick={() => handleClassClick()}>
        <ListItemIcon className="centered-container-row icon-left-margin">
          <FontAwesomeIcon icon={faUsers} size="1x" />
        </ListItemIcon>
        <ListItemText primary={class_name} />
        {class_data?.length > 1 && (classOpen ? <ExpandLess /> : <ExpandMore />)}
      </ListItemButton>
      {class_data?.length > 1 && (
        <Collapse in={classOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {class_data.map((item) => (
              <ListItemButton key={item.id} sx={{ pl: 4 }}
                onClick={() => handleClassClick(item)}>
                {class_id == item.id && (
                  <ListItemIcon><FontAwesomeIcon icon={faCheck} /></ListItemIcon>
                )}
                <ListItemText primary={item.naziv} />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      )}
    </List>
  );

  const renderNVULogo = () => (
    <div style={{ marginTop: 0, fontSize: 14, fontStyle: 'italic', flexGrow: 1 }} className="centered-container">
      <div>Inicijativa NVU &quot;Učionica u pokretu&quot;</div>
    </div>
  );

  const renderContent = () => (
    <div style={{ width: '100%' }}>
      {selectedKey === 'marks' && !subjectSelected && (
        <MarksMUI marks={marks} onRowSelected={handleRowSelected}
          onChangeType={onChangeType} type_id={typeId} mark_types={markTypes} />
      )}
      {selectedKey === 'marks' && subjectSelected && (
        <MarksDetailsMUI marks={marksDetails} subject_name={subjectName}
          onBack={() => setSubjectSelected(false)} />
      )}
      {selectedKey === 'attendance' && (
        <Attendance attendance={attendance} />
      )}
      {selectedKey === 'conduct' && (
        <Conduct conduct={conduct} />
      )}
      {selectedKey === 'discipline' && (
        <Discipline discipline={discipline} />
      )}
      {selectedKey === 'ext_test' && (
        <ExtTest marks={extTest} />
      )}
      {selectedKey === 'parent_coop' && (
        <ParentCoop parent_coop={parentCoop} />
      )}
      {selectedKey === 'notifications' && (
        <Notifications notifications={notifications} />
      )}
      {selectedKey === 'contact' && (
        <Contact class_id={class_id} />
      )}
    </div>
  );

  const getMobileTitle = () => {
    if (selectedKey === 'marks' && subjectSelected) return subjectName;
    return 'Dnevnik';
  };

  return (
    <div>
      <div>
        <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
          <Toolbar>
            {!isMobile && (
              <Typography variant="h6" sx={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-start', alignItems: 'flex-start' }}>
                Dnevnik
              </Typography>
            )}
            {isMobile && (
              <IconButton edge="start" color="inherit" sx={{ mr: 2 }}
                onClick={() => setLeft(true)}>
                <MenuIcon />
              </IconButton>
            )}
            {isMobile && (
              <Typography variant="h6" noWrap sx={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-start', alignItems: 'flex-start' }}>
                {getMobileTitle()}
              </Typography>
            )}
            {!isMobile && (
              <>
                <div style={{ marginRight: 10 }}>{user}</div>
                <div style={{ marginRight: 20 }}>
                  <FontAwesomeIcon icon={faUserGraduate} size="2x" />
                </div>
                <Tooltip title="Odjavi se" arrow>
                  <Button color="inherit" onClick={() => handleOptionSelected('logout')}>
                    <FontAwesomeIcon icon={faPowerOff} size="2x" />
                  </Button>
                </Tooltip>
              </>
            )}
            {isMobile && selectedKey === 'marks' && subjectSelected && (
              <IconButton color="inherit" onClick={() => setSubjectSelected(false)}>
                <FontAwesomeIcon icon={faArrowLeft} size="1x" />
              </IconButton>
            )}
          </Toolbar>
        </AppBar>

        {/* Desktop permanent drawer - only rendered on desktop */}
        {!isMobile && (
          <Drawer
            variant="permanent"
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              '& .MuiDrawer-paper': { width: drawerWidth },
            }}
          >
            <Toolbar />
            {renderMenuList(false)}
            <Divider />
            {renderLogoutList()}
            <Divider />
            {renderClassList()}
            {renderNVULogo()}
          </Drawer>
        )}

        {/* Mobile swipeable drawer - opens on hamburger button */}
        <SwipeableDrawer open={left} onClose={() => setLeft(false)} onOpen={() => setLeft(true)}>
          <div style={{ width: 280 }} role="presentation" onKeyDown={() => setLeft(false)}>
            <div style={{
              height: 75, backgroundImage: `url(${BackgroundHeader})`,
              display: 'flex', justifyContent: 'flex-start', alignItems: 'center', padding: 10,
            }}>
              <div style={{ color: 'white', marginRight: 20, marginLeft: 10 }}>
                <FontAwesomeIcon icon={faUserGraduate} size="2x" />
              </div>
              <div style={{ color: 'white', fontWeight: 'bold' }}>{user}</div>
            </div>
            {renderMenuList(true)}
            <Divider />
            {renderLogoutList()}
            <Divider />
            {renderClassList()}
            {renderNVULogo()}
          </div>
        </SwipeableDrawer>

        {loading && <Spinner />}

        <main style={{
          padding: isMobile ? 0 : 16,
          marginLeft: isMobile ? 0 : drawerWidth,
        }}>
          <Toolbar />
          {renderContent()}
        </main>
        <div style={{ height: 50 }} />
      </div>

      {/* Footer */}
      <div className="footer-fixed centered-container" style={{ zIndex: 999999999 }}>
        <div style={{ marginLeft: 10, fontSize: isMobile ? 10 : 'inherit' }}>
          Copyright &copy; Ministarstvo prosvjete, nauke i inovacija Crne Gore 2026
        </div>
        <div className="centered-container-row">
          <div style={{ marginRight: 10, fontSize: isMobile ? 10 : 'inherit' }}>U saradnji sa </div>
          <a href="http://www.telekom.me" style={{ margin: 3 }} className="centered-container-row">
            <img src={LogoImg} style={{ marginRight: 10, height: 35 }} alt="Telekom" />
          </a>
        </div>
      </div>
    </div>
  );
}
